import { Select, type SelectProps } from '@aigc/antd';
import { usePagingQueryHelper } from '@aigc/hooks';
import { useQuery } from '@tanstack/react-query';
import { Image } from 'antd';
import { useMemo, useState } from 'react';
import {
  queryAigcAssets,
  querySystemAigcAssets,
  type AssetQueryParams,
} from '../api';

interface AssetSelectItem {
  id: string;
  name: string;
  url: string;
}

export const VirtualAvatarSelect = (props: SelectProps<string>) => {
  const [searchName, setSearchName] = useState('');

  // 系统资产——全量获取，无鉴权、无分页
  const { data: systemAssets, isLoading: systemLoading } = useQuery(
    querySystemAigcAssets,
  );

  // 部门资产——分页（每页 50），支持名称搜索
  const { pagination, queryParams, queryMany } =
    usePagingQueryHelper<AssetQueryParams>({
      defaultQueryParams: { status: ['active'] },
      defaultPagination: { size: 50 },
    });
  const { data: deptAssets, isLoading: deptLoading } = useQuery({
    ...queryAigcAssets(pagination, queryParams),
  });

  // 合并：系统资产本地过滤 + 部门资产（服务端已过滤）
  const items = useMemo<AssetSelectItem[]>(() => {
    const keyword = searchName.toLowerCase();
    const system = (systemAssets ?? [])
      .filter((a) => !keyword || a.name.toLowerCase().includes(keyword))
      .map<AssetSelectItem>((item) => ({
        id: item.id,
        name: item.name,
        url: item.url,
      }));
    const dept = (deptAssets?.items ?? []).map<AssetSelectItem>((item) => ({
      id: item.asset_id,
      name: item.name,
      url: item.filename ?? '',
    }));
    return [...system, ...dept];
  }, [systemAssets, deptAssets, searchName]);

  const isLoading = systemLoading || deptLoading;

  return (
    <Select
      loading={isLoading}
      showSearch={{
        filterOption: false,
        onSearch: (value) => {
          setSearchName(value);
          // 部门资产走 API 搜索，系统资产由上面的 useMemo 本地过滤
          queryMany((prev) => ({ ...prev, name: value || undefined }));
        },
      }}
      {...props}
      tagRender={(tagProps) => {
        const item = items.find((i) => i.id === tagProps.value);
        if (!item) return tagProps.value;
        return (
          <Image
            onClick={(e) => {
              e.stopPropagation();
            }}
            src={item.url}
            width={128}
            height={128}
            classNames={{ image: 'object-cover' }}
          ></Image>
        );
      }}
      options={items.map((item) => ({
        value: item.id,
        label: (
          <div className="flex items-center space-x-2">
            <Image
              src={item.url}
              width={128}
              height={128}
              preview={false}
              classNames={{ image: 'object-cover' }}
            ></Image>
            <div>{item.name}</div>
          </div>
        ),
      }))}
    ></Select>
  );
};
